/**
 * EmployeePublicProfile.jsx
 * ════════════════════════════════════════════════════════════════
 * Секція "Публічний профіль" для картки працівника.
 * Використовує спільний RichEditor.
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faEyeSlash, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import RichEditor from '../../../../../../components/RichEditor/Richeditor';
import ExpandableText from '../../../../../../components/ExpandableText/ExpandableText';

const BIO_MAX = 3000;

const EmployeePublicProfile = function({ publicProfile, isEditing, onChange }) {
    const [expanded, setExpanded] = useState(false);

    var profile   = publicProfile || {};
    var isPublic  = !!profile.isPublic;
    var bio       = profile.bio            || '';
    var publicPos = profile.publicPosition || '';

    // ── Режим перегляду ────────────────────────────────────────────────────────

    if (!isEditing) {
        var hasContent = isPublic || bio || publicPos;
        if (!hasContent) return null;

        return (
            <div className="section" style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <h6 style={{ margin: 0 }}>Публічний профіль</h6>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 9px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        background: isPublic ? '#ecfdf5' : '#f3f4f6',
                        color:      isPublic ? '#059669' : '#6b7280',
                        border: '1px solid ' + (isPublic ? '#a7f3d0' : '#e5e7eb'),
                    }}>
                        <FontAwesomeIcon icon={isPublic ? faGlobe : faEyeSlash} style={{ fontSize: 11 }} />
                        {isPublic ? 'Показується публічно' : 'Прихований'}
                    </span>
                </div>

                {publicPos && (
                    <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Посада для сайту</div>
                        <div style={{ fontSize: 14, color: '#111827' }}>{publicPos}</div>
                    </div>
                )}

                {bio && (
                    <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Про себе</div>
                        <div style={{
                            background: '#f9fafb', borderRadius: 8,
                            padding: '12px 16px',
                            borderLeft: '3px solid #e5e7eb',
                        }}>
                            <ExpandableText
                                html={bio}
                                previewHeight={96}
                                style={{ fontSize: 14, color: '#374151' }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── Режим редагування ──────────────────────────────────────────────────────

    return (
        <div className="section" style={{ marginTop: 8 }}>
            {/* Заголовок з toggle */}
            <div
                style={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    marginBottom: expanded ? 18 : 0,
                    padding: '4px 0',
                }}
                onClick={function() { setExpanded(function(v) { return !v; }); }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h6 style={{ margin: 0 }}>Публічний профіль</h6>

                    {/* Toggle isPublic */}
                    <div
                        onClick={function(e) { e.stopPropagation(); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                        <button
                            type="button"
                            onClick={function() { onChange({ isPublic: !isPublic }); }}
                            style={{
                                width: 40, height: 22, borderRadius: 11, border: 'none',
                                background: isPublic ? '#059669' : '#d1d5db',
                                position: 'relative', cursor: 'pointer',
                                transition: 'background 0.2s', flexShrink: 0,
                            }}
                        >
                            <span style={{
                                position: 'absolute', top: 3,
                                left: isPublic ? 21 : 3,
                                width: 16, height: 16, borderRadius: '50%',
                                background: '#fff', transition: 'left 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            }} />
                        </button>
                        <span style={{
                            fontSize: 12, fontWeight: 600,
                            color: isPublic ? '#059669' : '#6b7280',
                        }}>
                            {isPublic ? 'Показується на сайті' : 'Прихований'}
                        </span>
                    </div>
                </div>

                <FontAwesomeIcon
                    icon={expanded ? faChevronUp : faChevronDown}
                    style={{ fontSize: 13, color: '#9ca3af' }}
                />
            </div>

            {expanded && (
                <div>
                    {/* Посада для сайту */}
                    <div style={{ marginBottom: 16 }}>
                        <label style={{
                            fontSize: 12, color: '#111827',
                            display: 'block', marginBottom: 6, fontWeight: 500,
                        }}>
                            Посада для сайту
                            <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 4 }}>
                                (якщо відрізняється від штатної)
                            </span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={publicPos}
                            onChange={function(e) { onChange({ publicPosition: e.target.value }); }}
                            placeholder="Наприклад: Директорка, Старший вихователь"
                            maxLength={100}
                            style={{ height: 40, fontSize: 14 }}
                        />
                    </div>

                    {/* Біографія */}
                    <div>
                        <label style={{
                            fontSize: 12, color: '#111827',
                            display: 'block', marginBottom: 8, fontWeight: 500,
                        }}>
                            Про себе
                        </label>
                        <RichEditor
                            value={bio}
                            onChange={function(html) { onChange({ bio: html }); }}
                            placeholder="Розкажіть про цього спеціаліста: освіта, досвід, захоплення..."
                            maxLength={BIO_MAX}
                            minHeight={180}
                            maxHeight={420}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeePublicProfile;